// Shared, API-honest readiness model for note-to-claim rows.
// Every value here is derivable from the /note-to-claim/notes response — no invented data.

export const BLOCKERS = {
  unlinked: { label: "Link diagnosis pointer", short: "CPT line needs a diagnosis pointer", fix: "Auto-link the primary diagnosis to the unlinked CPT line — one pointer added, review & apply." },
  no_payer: { label: "Add payer / insurance", short: "No payer on file", fix: "Attach the payer revQ matched from eligibility, then re-scrub the claim." },
  no_dos:   { label: "Add date of service", short: "No date of service", fix: "Set the date of service read from the encounter, then re-run readiness." },
  no_icd:   { label: "Add a diagnosis", short: "No diagnosis coded", fix: "Add the diagnosis revQ read from the note — open to confirm the ICD-10." },
  no_cpt:   { label: "Code procedures", short: "No procedure coded yet", fix: "Code the procedure revQ detected in the note — open to confirm the CPT & units." },
};
export const BLOCKER_ORDER = ["unlinked", "no_payer", "no_dos", "no_icd", "no_cpt"];

export function readiness(n) {
  const cc = n.cpt_count || 0;
  const ic = n.icd_count || 0;
  const ins = n.insurance || {};
  const bo = (n.validation_report || {}).billing_output || {};
  const blockers = [];
  if (cc === 0) blockers.push("no_cpt");
  if (!n.date_of_service) blockers.push("no_dos");
  if (!(n.payer_name || ins.found)) blockers.push("no_payer");
  if (cc > 0 && ic === 0) blockers.push("no_icd");
  if (ic > 0 && (n.cpt_codes || []).some((c) => !((c.dx || []).length))) blockers.push("unlinked");
  const bucket = bo.ready === true ? "ready" : (cc === 0 ? "needs_coding" : "needs_review");
  return { blockers, bucket, pay: bo.payability || null };
}

export const estCharge = (n) => (n.cpt_codes || []).reduce((s, c) => s + (Number(c?.avg_paid?.amt) || 0), 0);
export const money = (v) => (v > 0 ? `$${Math.round(v).toLocaleString()}` : "—");
export const kMoney = (v) => (!v || v <= 0 ? "—" : v < 1000 ? `$${Math.round(v)}` : `$${(v / 1000).toFixed(1)}k`);

export const BUCKETS = [
  { key: "ready",        label: "Ready to submit", tone: "#34c759", sub: "Pipeline marked the 837 ready." },
  { key: "needs_review", label: "Needs review", tone: "#ff9f0a", sub: "Coded — a blocker needs a human." },
  { key: "needs_coding", label: "Needs coding", tone: "#8e8e93", sub: "Uploaded — no procedure coded yet." },
];
export const STATE_PILL = {
  ready: { label: "Ready", cls: "ready" },
  needs_review: { label: "Needs review", cls: "review" },
  needs_coding: { label: "Needs coding", cls: "coding" },
};

export const patientOf = (n) =>
  n.patient_name || [n.patient?.first_name, n.patient?.last_name].filter(Boolean).join(" ").trim() || "Unnamed patient";
export const payerOf = (n) => n.payer_name || n.insurance?.payer_name || null;
export const cptsOf = (n) => (n.cpt_codes || []).map((c) => c?.code).filter(Boolean);
export const memberIdOf = (n) => n.member_id || n.insurance?.member_id || null;
export const posOf = (n) => {
  const p = n.place_of_service;
  if (!p) return n.pos_code || null;
  return [p.description, p.code].filter(Boolean).join(" · ") || n.pos_code || null;
};
export const SOURCE_LABEL = { pdf: "PDF", multi: "Multi-doc", text: "Text / paste", dictation: "Dictation", televisit: "Telemedicine" };
// DA-72 — the notes list now carries visit_source ("dictation"/"televisit"); prefer it over the ingestion source_type.
export const sourceOf = (n) => SOURCE_LABEL[n.visit_source] || SOURCE_LABEL[n.source_type] || n.visit_source || n.source_type || "—";

// CSV of the given enriched rows ([{n, r}]). Estimates are corpus-avg references.
export function toCsv(enriched) {
  const head = ["Patient", "MRN", "Payer", "Member ID", "Date of service", "Place of service",
    "Source", "CPTs", "ICD count", "Readiness", "Blockers", "Est. charge (corpus avg-paid)"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = enriched.map(({ n, r }) => [
    patientOf(n), n.mrn || "", payerOf(n) || "", memberIdOf(n) || "", n.date_of_service || "",
    posOf(n) || "", sourceOf(n), cptsOf(n).join(" "), n.icd_count ?? 0,
    STATE_PILL[r.bucket].label, r.blockers.map((b) => BLOCKERS[b].label).join("; "),
    estCharge(n) > 0 ? Math.round(estCharge(n)) : "",
  ].map(esc).join(","));
  return [head.map(esc).join(","), ...lines].join("\n");
}

export function downloadCsv(enriched) {
  const url = URL.createObjectURL(new Blob([toCsv(enriched)], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url; a.download = `create-claims-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}
